import { t } from '../i18n';
import type {
  AcquisitionMetrics, ActivationMetrics, CostMetrics, DataEnvelope, FeatureUsageMetrics, OverviewMetrics,
  PriceComparisonMetrics, ReceiptMetrics, ReliabilityMetrics, RetentionMetrics,
} from '../types/analytics';

export type AnalyticsSectionId =
  | 'overview' | 'acquisition' | 'activation' | 'retention' | 'features'
  | 'receipts' | 'price-comparison' | 'costs' | 'reliability';

export interface SectionDataMap {
  overview: OverviewMetrics;
  acquisition: AcquisitionMetrics;
  activation: ActivationMetrics;
  retention: RetentionMetrics;
  features: FeatureUsageMetrics;
  receipts: ReceiptMetrics;
  'price-comparison': PriceComparisonMetrics;
  costs: CostMetrics;
  reliability: ReliabilityMetrics;
}

export type ExportSection = {
  [K in AnalyticsSectionId]: { id: K; envelope: DataEnvelope<SectionDataMap[K]> }
}[AnalyticsSectionId];

/** Section names in the active language (read when used, so a language switch applies). */
export const sectionLabels: Record<AnalyticsSectionId, string> = {
  get overview() { return t('v1.section.overview'); },
  get acquisition() { return t('v1.section.acquisition'); },
  get activation() { return t('v1.section.activation'); },
  get retention() { return t('v1.section.retention'); },
  get features() { return t('v1.section.features'); },
  get receipts() { return t('v1.section.receipts'); },
  get 'price-comparison'() { return t('v1.section.price-comparison'); },
  get costs() { return t('v1.section.costs'); },
  get reliability() { return t('v1.section.reliability'); },
};

/** `features` is the stable id of the Engagement section (its API endpoint and export filenames keep the old name). */
export const sectionOrder: AnalyticsSectionId[] = [
  'overview', 'acquisition', 'activation', 'features', 'retention', 'reliability', 'costs', 'receipts', 'price-comparison',
];

export function sectionFromPath(pathname: string): AnalyticsSectionId {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (segment === 'engagement') return 'features';
  return sectionOrder.includes(segment as AnalyticsSectionId) ? segment as AnalyticsSectionId : 'overview';
}
